/* eslint-disable no-useless-escape */
import type { LocalStorageDataT } from '@/constant/localStorageData'
import { persistentLocalStorageKeys } from '@/constant/localStorageData'
import type { SessionStorageDataT } from '@/constant/sessionStorageData'

type LocalStorageKeyT = keyof LocalStorageDataT & string
type SessionStorageKeyT = keyof SessionStorageDataT & string
type StorageValueT<K extends LocalStorageKeyT | SessionStorageKeyT> = K extends LocalStorageKeyT
  ? LocalStorageDataT[K]
  : K extends SessionStorageKeyT
    ? SessionStorageDataT[K]
    : never

type StorageItemT<K extends LocalStorageKeyT | SessionStorageKeyT> = {
  key: K
  value: StorageValueT<K>
}

const LOCAL_STORAGE_VERSION = 3
const SESSION_STORAGE_VERSION = 2

// @see 문서: https://www.notion.so/freewheelin/bda97550def44bcbb26e4df9a3d3bcb5
// 버전 업데이트할 때마다 문서 업데이트 부탁드립니다.
export class StorageService<
  K extends LocalStorageKeyT | SessionStorageKeyT = LocalStorageKeyT | SessionStorageKeyT,
> {
  private storage: Storage
  type: 'local' | 'session'
  targetVersion: LocalStorageDataT['VERSION']
  currentVersion: LocalStorageDataT['VERSION']

  constructor({ type }: { type: K extends LocalStorageKeyT ? 'local' : 'session' }) {
    this.type = type
    this.storage = type === 'local' ? localStorage : sessionStorage
    this.currentVersion = this.getItem('VERSION') ?? 1
    this.targetVersion = type === 'local' ? LOCAL_STORAGE_VERSION : SESSION_STORAGE_VERSION
  }

  setItem<K extends LocalStorageKeyT | SessionStorageKeyT>(k: K) {
    return (v: StorageValueT<K>) =>
      this.storage.setItem(k, typeof v === 'string' ? v : JSON.stringify(v))
  }

  setItems(items: StorageItemT<K>[]) {
    items.forEach(({ key, value }) => this.setItem(key)(value))
  }

  getItem<K extends LocalStorageKeyT | SessionStorageKeyT>(k: K): StorageValueT<K> | null {
    const item = this.storage.getItem(k)
    try {
      // TODO: v1 코드
      if (item === 'TRUE' || item === 'FALSE') {
        this.setItem(k as LocalStorageKeyT)(item === 'TRUE')
        return (item === 'TRUE') as StorageValueT<K>
      }

      // TODO: v1 코드
      if (item === JSON.stringify('TRUE') || item === JSON.stringify('FALSE')) {
        this.setItem(k as LocalStorageKeyT)(item === JSON.stringify('TRUE'))
        return (item === JSON.stringify('TRUE')) as StorageValueT<K>
      }

      if (k === 'SAVED_LOGIN_ID') {
        return item as StorageValueT<K> | null
      }

      return item ? JSON.parse(item) : null
    } catch (_) {
      return item as StorageValueT<K>
    }
  }

  removeItem<K extends LocalStorageKeyT | SessionStorageKeyT>(k: K) {
    this.storage.removeItem(k)
  }

  // unRegisterPersistentItems(keyArr: K[]) {
  //   this.keyOfPersistentItems = this.keyOfPersistentItems.filter((pk) => !keyArr.includes(pk))
  // }

  clear() {
    this.storage.clear()
  }

  clearExceptPermanent() {
    const permanentItems = Object.keys(this.storage)
      .filter((key) => {
        // '팝업만료_T1234'에서 '팝업만료'만 있어도 남기기 위해 string include를 체크
        return persistentLocalStorageKeys.some((pk) => key.includes(pk)) && !!this.getItem(key as K)
      })
      .map((key) => ({
        key,
        value: this.getItem(key as K),
      }))

    this.clear()
    this.setItems(permanentItems as StorageItemT<K>[])
  }

  async initializeStorage() {
    return new Promise((resolve, reject) => {
      try {
        // 버저닝 이전 데이터 migration
        const currentVersion = this.currentVersion
        const targetVersion = this.targetVersion
        if (currentVersion < targetVersion) {
          this.migrateToLatest()
        }

        // set storage version to latest
        this.setItem('VERSION')(targetVersion)

        resolve(true)
      } catch (e) {
        console.error(e)
        reject(e.message)
      }
    })
  }

  migrateToLatest() {
    // 모든 하위 버전에서 타야하는 로직
    {
      if (this.type === 'local') {
        const rankOptions = this.getItem('REPORT_RANK_OPTIONS')
        if (rankOptions) {
          const newRankOptions = { options: rankOptions, hasRank: false }
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          this.setItem('REPORT_RANK_OPTIONS')(newRankOptions)
        }
      }
    }

    // v1 changelog - v1일 경우 반드시 타야하는 로직
    if (this.currentVersion === 1) {
      // localStorage, sessionStorage 공통
      // 1. v1의 stringified string "와 \벗겨낸다.
      const possibleStringfiedKeys = [
        'SAVED_LOGIN_ID',
        'x-auth-token',
        'print-api',
        'teacher-id',
        'TEACHER_ID',
        'X_AUTH_TOKEN',
        'PRINT_API',
        'ASSIGN_TYPE',
        'DEV_API',
        'LOGIN_PATH',
        'SMS_RECIPIENT',
        'SMS_TYPE',
        'TODAY_FIRST_USE_HISTORY',
        'RECENT_PRINT_NAME',
        '수업_아코디언_학생타입',
        '출결_아코디언_학생타입',
      ]

      Object.entries(this.storage).forEach(([key, value]) => {
        if (possibleStringfiedKeys.includes(key)) {
          this.setItem(key as K)(value.replace(/\"|\\/g, ''))
        }
      })

      // localStorage only
      if (this.type === 'local') {
        // 2. 1번 후, v1의 ""로 감싸지지 않은 Date string을 timestamp로 변환한다.
        const dateStr = this.getItem('TODAY_FIRST_USE_HISTORY')
        if (dateStr) {
          const date = new Date(dateStr).getTime()
          this.setItem('TODAY_FIRST_USE_HISTORY')(date)
        }

        // 3. v1의 다음 값들의 현재 저장된 옵션을 true로 고정한다.
        const chapterOption = this.getItem('BY_CHAPTER_OPTION')
        if (chapterOption) {
          chapterOption.onlyAutoScorable = true
          this.setItem('BY_CHAPTER_OPTION')(chapterOption)
        }

        const schoolTestOption = this.getItem('BY_SCHOOL_TEST_OPTION')
        if (schoolTestOption) {
          this.setItem('BY_SCHOOL_TEST_OPTION')(schoolTestOption)
        }

        const practiceOption = this.getItem('BY_PRACTICE_OPTION')
        if (practiceOption) {
          practiceOption.onlyAutoScorable = true
          this.setItem('BY_PRACTICE_OPTION')(practiceOption)
        }

        // const workbookOption = this.getItem('BY_WORKBOOK_DEFAULT_OPTION')
        // if (workbookOption) {
        //   workbookOption.onlyAutoScorable = true
        //   this.setItem('BY_WORKBOOK_DEFAULT_OPTION')(workbookOption)
        // }

        // const workbookTwinOption = this.getItem('BY_WORKBOOK_TWIN_OPTION')
        // if (workbookTwinOption) {
        //   workbookTwinOption.onlyAutoScorable = true
        //   this.setItem('BY_WORKBOOK_TWIN_OPTION')(workbookTwinOption)
        // }
      }
    }
  }

  // TODO: 공통화하다가 시간없어서..
  // static mapUtil<
  //   T extends LocalStorageKeyT | SessionStorageKeyT,
  //   U = T extends LocalStorageKeyT
  //     ? LocalStorageDataT[T]
  //     : T extends SessionStorageKeyT
  //       ? SessionStorageDataT[T]
  //       : never,
  //   V = U extends object ? U : never,
  //   K = U extends object ? (keyof U extends T ? keyof U : never) :never,
  // >(key: K) {
  //   return {
  //     getMap: (storageService: StorageService<K>) => {
  //       const storageData = storageService.getItem(key)
  //       return storageData ? new Map<K, V>(Object.entries(storageData)) : new Map<K, U>()
  //     },
  //     setMap: (storageService: StorageService) => (map: Map<K, U>) => {
  //       if (map.size === 0) {
  //         storageService.removeItem(key)
  //       } else {
  //         storageService.setItem(key)(Object.fromEntries(map))
  //       }
  //     },
  //     getItem: (storageService: StorageService) => (value) => {
  //       const map = this.mapUtil(key).getMap(key)(storageService)
  //       return map.get(value)
  //     },
  //     setItem: (storageService: StorageService) => (value) => {
  //       const map = this.getMap(key)(storageService)
  //       map.set(key, value)
  //       this.map.setMap(storageService)(map)
  //     },
  //   }
  // }
}

export const localStorageService = new StorageService<LocalStorageKeyT>({
  type: 'local',
})

export const sessionStorageService = new StorageService<SessionStorageKeyT>({ type: 'session' })
